(function () {
  "use strict";

  if (customElements.get("bb-related-recommendations")) return;

  var model = window.BBRelatedProductsModel;

  if (!model) {
    console.error("BadgeBuzz related-products model is unavailable.");
    return;
  }

  function parseIds(value) {
    try {
      var parsed = JSON.parse(value || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  class BBRelatedRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.context === "cart" && !this.claimCartOwner()) {
        this.hideSection();
        return;
      }

      if (this.dataset.sourceMode === "manual") {
        this.showFallback();
        return;
      }

      if (!("IntersectionObserver" in window)) {
        this.load();
        return;
      }

      this.loadStarted = false;
      this.startLoading = () => {
        if (!this.isConnected || this.loadStarted) return;
        this.loadStarted = true;
        window.clearTimeout(this.backgroundTimer);
        window.removeEventListener("load", this.scheduleBackgroundLoad);
        this.observer?.disconnect();
        this.load();
      };
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          this.startLoading();
        },
        {rootMargin: "0px 0px 1200px 0px"},
      );
      this.observer.observe(this);
      this.scheduleBackgroundLoad = () => {
        this.backgroundTimer = window.setTimeout(this.startLoading, 300);
      };
      if (document.readyState === "complete") this.scheduleBackgroundLoad();
      else window.addEventListener("load", this.scheduleBackgroundLoad, {once: true});
    }

    disconnectedCallback() {
      window.clearTimeout(this.backgroundTimer);
      if (this.scheduleBackgroundLoad) window.removeEventListener("load", this.scheduleBackgroundLoad);
      if (this.observer) this.observer.disconnect();
      if (this.controller) this.controller.abort();
      if (window.BBRelatedProductsCartOwner === this) {
        window.BBRelatedProductsCartOwner = null;
      }
    }

    claimCartOwner() {
      if (
        window.BBRelatedProductsCartOwner &&
        window.BBRelatedProductsCartOwner !== this
      ) {
        return false;
      }

      window.BBRelatedProductsCartOwner = this;
      return true;
    }

    get sectionElement() {
      return this.closest(".bb-related-products");
    }

    get resultsElement() {
      return this.querySelector("[data-bb-results]");
    }

    get skeletonElement() {
      return this.querySelector(".bb-related-products__skeleton");
    }

    get sourceIds() {
      return model.sourceIdsForRequest(
        parseIds(this.dataset.sourceProductIds),
        3,
      );
    }

    get excludedIds() {
      return model.uniqueIds(
        parseIds(this.dataset.excludedProductIds).concat(this.sourceIds),
      );
    }

    get limit() {
      return Math.min(4, Math.max(2, Number(this.dataset.limit) || 4));
    }

    async load() {
      if (this.loading) return;

      this.loading = true;
      this.setAttribute("aria-busy", "true");

      if (!this.sourceIds.length) {
        this.showFallback();
        return;
      }

      this.controller = new AbortController();

      try {
        var cards = await this.loadRecommendationCards();

        if (cards.length) {
          this.showCards(cards, "recommendation");
        } else {
          this.showFallback();
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("BadgeBuzz related products could not load.", error);
          this.showFallback();
        }
      } finally {
        this.loading = false;
      }
    }

    async loadRecommendationCards() {
      var selectedIds = [];
      var cardsById = Object.create(null);

      for (var index = 0; index < this.sourceIds.length; index += 1) {
        var sourceId = this.sourceIds[index];
        var url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set("product_id", sourceId);
        url.searchParams.set("section_id", this.dataset.sectionId);

        var response;

        try {
          response = await fetch(url.toString(), {
            signal: this.controller.signal,
            headers: {"X-Requested-With": "XMLHttpRequest"},
          });

          if (!response.ok) {
            throw new Error("Recommendation request failed with " + response.status);
          }
        } catch (error) {
          if (error.name === "AbortError") throw error;
          console.error("BadgeBuzz recommendation request failed.", error);
          continue;
        }

        var html = await response.text();
        var documentFragment = new DOMParser().parseFromString(
          html,
          "text/html",
        );
        var fragment = documentFragment.querySelector(
          "[data-bb-recommendation-fragment]",
        );

        if (!fragment) continue;

        var candidateCards = Array.from(
          fragment.querySelectorAll("[data-bb-recommendation-card]"),
        );
        var candidateIds = candidateCards.map(function (card) {
          var id = String(card.dataset.productId || "");
          if (id) cardsById[id] = card;
          return id;
        });

        selectedIds = model.mergeCandidateIds(
          selectedIds,
          candidateIds,
          this.excludedIds,
          this.limit,
        );

        if (selectedIds.length >= this.limit) break;
      }

      return selectedIds
        .map(function (id) {
          return cardsById[id];
        })
        .filter(Boolean);
    }

    showFallback() {
      var template = this.querySelector("[data-bb-fallback-template]");

      if (!template) {
        this.hideSection();
        return;
      }

      var fallbackCards = Array.from(
        template.content.querySelectorAll("[data-bb-recommendation-card]"),
      );
      var cardsById = Object.create(null);
      var candidateIds = fallbackCards.map(function (card) {
        var id = String(card.dataset.productId || "");
        if (id) cardsById[id] = card;
        return id;
      });
      var selectedIds = model.mergeCandidateIds(
        [],
        candidateIds,
        this.excludedIds,
        this.limit,
      );
      var cards = selectedIds
        .map(function (id) {
          return cardsById[id];
        })
        .filter(Boolean);

      if (cards.length) {
        this.showCards(cards, "fallback");
      } else {
        this.hideSection();
      }

      this.loading = false;
    }

    showCards(cards, source) {
      var results = this.resultsElement;

      if (!results || !cards.length) {
        this.hideSection();
        return;
      }

      var fragment = document.createDocumentFragment();
      cards.forEach(function (card) {
        fragment.appendChild(card.cloneNode(true));
      });
      results.replaceChildren(fragment);
      results.hidden = false;

      if (this.skeletonElement) this.skeletonElement.hidden = true;
      if (this.sectionElement) this.sectionElement.hidden = false;
      this.setAttribute("aria-busy", "false");
      this.removeAttribute("aria-label");

      this.dispatchEvent(
        new CustomEvent("bb:related-products-loaded", {
          bubbles: true,
          detail: {
            source: source,
            count: cards.length,
            context: this.dataset.context || "page",
          },
        }),
      );
    }

    hideSection() {
      if (this.skeletonElement) this.skeletonElement.hidden = true;
      if (this.resultsElement) this.resultsElement.hidden = true;
      if (this.sectionElement) this.sectionElement.hidden = true;
      this.setAttribute("aria-busy", "false");
      this.removeAttribute("aria-label");
      this.loading = false;
    }
  }

  customElements.define('bb-related-recommendations', BBRelatedRecommendations);
})();
